import { useState } from "react"
import { Button } from "@/components/ui/button"
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Lock } from "lucide-react"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "./ui/form"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { passwordSchema, type ChangeSPasswordForm } from "@/schemas/passwordSchema"
import { updatePassword, verifyNumber, verifyPassword } from "@/actions/password"
import { StatusCode } from "@/types"
import { Spinner } from "@/components/ui/spinner"
import { toast } from "sonner"

export const PasswordChange = () => {
    const [loading, setLoading] = useState(false)
    const [open, isOpen] = useState(false)

    const password = useForm<ChangeSPasswordForm>({
        resolver: zodResolver(passwordSchema),
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            username: "",
            phoneNumber: "",
            currentPassword: "",
            confirmPassword: "",
        },
    })

    const validateUserAndPhone = async () => {
        const username = password.getValues("username")
        const phoneNumber = password.getValues("phoneNumber")

        if (!username || !phoneNumber) return

        const status = await verifyNumber(username, phoneNumber)

        if (status !== StatusCode.OK) {
            password.setError("phoneNumber", {
                type: "manual",
                message: "Usuario y número de celular no coinciden",
            })
        } else {
            password.clearErrors("phoneNumber")
        }
    }

    const validateNewPassword = async () => {
        const newPassword = password.getValues("currentPassword")
        const confirmPassword = password.getValues("confirmPassword")

        if (!newPassword || !confirmPassword) return
        if (password.formState.errors.confirmPassword) return

        const { status, message } = await verifyPassword(newPassword, confirmPassword)
        console.log(status, message)

        if (status !== StatusCode.OK) {
            password.setError("currentPassword", {
                type: "manual",
                message: message ?? "La contraseña no cumple las reglas",
            })
        }
        else {
            password.clearErrors(["currentPassword", "confirmPassword"])
        }
    }

    const handleOnSubmit = async (values: ChangeSPasswordForm) => {
        if (loading) return

        try {
            setLoading(true)

            const { status, message } = await updatePassword(
                values.username,
                values.currentPassword
            )

            if (status === StatusCode.OK) {
                password.reset()
                toggleOpen()

                toast.success("Contraseña actualizada")
                return
            }

            password.setError("root", {
                type: "manual",
                message: message ?? "Ocurrió un error inesperado",
            })

        } finally {
            setLoading(false)
        }
    }

    const toggleOpen = () => {
        isOpen(current => !current)
        password.reset()
    }

    return (
        <Dialog open={open} onOpenChange={toggleOpen}>
            <DialogTrigger asChild>
                <Button className="cursor-pointer w-full h-10" variant="outline">
                    <Lock />
                    Cambiar contraseña
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-106.25">
                <DialogHeader>
                    <DialogTitle>Cambiar Contraseña</DialogTitle>
                    <DialogDescription>
                        Ingresa tu información para cambiar tu contraseña
                    </DialogDescription>
                </DialogHeader>
                {password.formState.errors.root && (
                    <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600">
                        {password.formState.errors.root.message}
                    </div>
                )}
                <Form {...password}>
                    <form className="space-y-4" onSubmit={password.handleSubmit(handleOnSubmit)}>
                        <FormField control={password.control} name="username" render={({ field }) => (
                            <FormItem>
                                <FormLabel>Usuario</FormLabel>
                                <FormControl>
                                    <Input {...field} type="text" placeholder="Ingresa tu nombre de usuario" autoComplete="username" />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )} />

                        <FormField control={password.control} name="phoneNumber" render={({ field }) => (
                            <FormItem>
                                <FormLabel>Número de Celular</FormLabel>
                                <FormControl>
                                    <Input {...field} type="text" placeholder="123 456 7890" autoComplete="phoneNumber"
                                        onBlur={async () => {
                                            field.onBlur()
                                            await validateUserAndPhone()
                                        }} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )} />

                        <FormField control={password.control} name="currentPassword" render={({ field }) => (
                            <FormItem>
                                <FormLabel>Nueva Contraseña</FormLabel>
                                <FormControl>
                                    <Input {...field} type="password" placeholder="Ingresa tu nueva contraseña" autoComplete="new-password" />
                                </FormControl>
                            </FormItem>
                        )} />


                        <FormField control={password.control} name="confirmPassword" render={({ field }) => (
                            <FormItem>
                                <FormLabel>Confirmar Contraseña</FormLabel>
                                <FormControl>
                                    <Input {...field} type="password" placeholder="Confirma tu nueva contraseña" autoComplete="new-password"
                                        onBlur={async () => {
                                            field.onBlur()
                                            await validateNewPassword()
                                        }} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )} />
                        <DialogFooter>
                            <DialogClose asChild>
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={loading}
                                >
                                    Cancelar
                                </Button>
                            </DialogClose>

                            <Button
                                type="submit"
                                className="bg-red-500 hover:bg-red-600 transition"
                                disabled={!password.formState.isValid || loading}
                            >
                                {loading ? <Spinner /> : "Cambiar Contraseña"}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default PasswordChange
